import angular from 'angular'

import deline from '../utils/deline'

import {
  noop, last, first, concat, compact, isArray,
  uniqueId, isString, stubFalse, fromPairs, dropRight,
  isBoolean, isFunction, isUndefined, escapeRegExp
} from 'lodash-es'

import { isText, isEmpty, isPromise, defaultTo } from '../utils/bits'

angular
  .module('ggs.gallery-styler-new')
  .factory('GSNApplyFuncTmpl', GSNApplyFuncTmplProvider)
  .factory('GSNApplyLinkTmpl', GSNApplyLinkTmplProvider)
  .factory('GSNApplyStyleTmpl', GSNApplyStyleTmplProvider)
  .factory('GSNApplyScriptTmpl', GSNApplyScriptTmplProvider)

// applyFuncTmpl

function GSNApplyFuncTmplProvider(DefineHandler) {

  return function GSNApplyFuncTmpl({
    propKeys,
    userFunc,
    afterFunc,
    ownerTag,
    destroy
  }) {

    const trueKeys = isArray(propKeys)
      ? propKeys
      : [propKeys]

    const trueSign = deline(`
      GSNApplyFuncTmpl
        [ownerTag=${ownerTag}]
        [propKeys=${trueKeys}]`)

    const wrapH = DefineHandler(trueSign)
    const apply = wrapH('applyFunc', applyFuncTmpl)

    apply.tmplType = 'applyFunc'
    apply.propKeys = trueKeys
    apply.ownerTag = ownerTag
    apply.destroy = destroy ?? noop
    apply.destroyed = false
    return apply

    function applyFuncTmpl(...values) {
      const res = userFunc(...values)
      if (!isFunction(afterFunc)) {
        return res
      }

      return isPromise(res)
        ? res.then(() => afterFunc(...values))
        : afterFunc(...values)
    }
  }
}

// applyLinkTmpl

function GSNApplyLinkTmplProvider(domh, addIdPrefix, DefineHandler) {

  return function GSNApplyLinkTmpl({
    propKeys,
    allowFunc,
    linkFunc,
    ownerTag
  }) {

    const trueKeys = isArray(propKeys)
      ? propKeys
      : [propKeys]

    const trueSign = deline(`
      GSNApplyLinkTmpl
        [ownerTag=${ownerTag}]
        [propKeys=${trueKeys}]`)

    const wrapH = DefineHandler(trueSign)
    const apply = wrapH('applyLink', applyLinkTmpl)

    apply.tmplType = 'applyLink'
    apply.propKeys = trueKeys
    apply.ownerTag = ownerTag
    apply.destroy = noop
    apply.destroyed = false
    return apply

    function applyLinkTmpl(...values) {
      // check allowFunc
      if (!allowFunc(...values)) {
        return
      }

      // then add new one
      const href = linkFunc(...values)
      if (!isText(href) && !isEmpty(href)) {
        throw new Error(`${trueSign}: [href=${href}] must be a string or empty!`)
      }

      if (isText(href) && !domh.has(document, `link[href="${href}"]`)) {
        const linkId = uniqueId(addIdPrefix(`applyLinkTmpl_propKeys-${trueKeys}_`))
        const link = domh.createLink(linkId).attach()
        link.href = href
      }
    }
  }
}

// applyStyleTmpl

function GSNApplyStyleTmplProvider(domh, addIdPrefix, DefineHandler) {

  return function GSNApplyStyleTmpl({
    propKeys,
    castKeys,
    allowKey,
    allowFunc,
    styleFunc,
    allowEmpty,
    styleConst,
    styleTmpl,
    stylePlh,
    afterFunc,
    ownerTag,
    destroy
  }) {

    const trueKeys = isArray(propKeys)
      ? propKeys
      : compact([propKeys])

    const trueCastKeys = isFunction(castKeys)
      ? { [trueKeys[0]]: castKeys }
      : isArray(castKeys)
        ? fromPairs(castKeys.map((castKey, index) => [trueKeys[index], castKey]))
        : defaultTo(castKeys, {})

    const trueSign = deline(`
      GSNApplyStyleTmpl
        [ownerTag=${ownerTag}]
        ${ isString(allowKey) ? `[allowKey=${allowKey}]` : ''}
        ${ trueKeys.length !== 0 ? `[propKeys=${trueKeys}]` : ''}`)

    const trueAllowFunc = isUndefined(allowKey) && isUndefined(allowFunc)
      ? () => true
      : isString(allowKey)
        ? (...values) => !!last(values)
        : allowFunc

    const truePlh = RegExp(escapeRegExp(stylePlh), 'g')
    const tmplFunc = (v) => styleTmpl.replace(truePlh, v)
    const trueStyleFunc = isString(styleConst)
      ? (...values) => trueAllowFunc(...values) ? styleConst : null
      : isString(styleTmpl)
        ? (...values) => trueAllowFunc(...values) ? tmplFunc(...values) : null
        : (...values) => trueAllowFunc(...values) ? styleFunc(...values) : null

    const hasEmpties = isString(styleConst)
      ? stubFalse
      : isString(styleTmpl)
        ? (...values) => isEmpty(first(values))
        : isString(allowKey)
          ? (...values) => dropRight(values, 1).some(isEmpty)
          : (...values) => values.some(isEmpty)

    const trueAllowEmpty = isBoolean(allowEmpty)
      ? allowEmpty
      : isFunction(styleFunc)

    const styleId = uniqueId(addIdPrefix(deline(`
      applyStyleTmpl_
        ${allowKey ? `allowKey-${allowKey}_` : ''}
        ${trueKeys.length !== 0 ? `propKeys-${trueKeys}_` : ''}`)))

    const style = domh.createStyle(styleId).attach()
    const wrapH = DefineHandler(trueSign)
    const apply = wrapH('applyStyle', applyStyleTmpl)

    apply.tmplType = 'applyStyle'
    apply.propKeys = compact(concat(trueKeys, allowKey))
    apply.ownerTag = ownerTag
    apply.destroy = applyDestroySequence
    apply.destroyed = false
    return apply

    function applyDestroySequence() {
      style.detach()
      if (isFunction(destroy)) {
        return destroy()
      }
    }

    function applyStyleTmpl(...values) {
      // cast values
      for (let index = 0, length = apply.propKeys.length; index !== length; index++) {
        const propKey = apply.propKeys[index]
        const castKey = trueCastKeys[propKey]
        if (isFunction(castKey)) {
          values[index] = castKey(values[index])
        }
      }

      // delete old cssRule
      style.clear()

      // then add new one
      if (!hasEmpties(...values) || trueAllowEmpty) {
        const cssRule = trueStyleFunc(...values)
        if (!isText(cssRule) && !isEmpty(cssRule)) {
          throw new Error(`${trueSign}: [cssRule=${cssRule}] must be a string or empty!`)
        }

        if (isText(cssRule)) {
          style.local.important(cssRule)
        }
      }

      // then call afterFunc
      if (isFunction(afterFunc)) {
        return afterFunc(...values)
      }
    }
  }
}

// applyScriptTmpl

function GSNApplyScriptTmplProvider(domh, addIdPrefix, DefineHandler) {

  return function GSNApplyScriptTmpl({
    propKeys,
    allowKey,
    scriptTmpl,
    scriptPlh,
    ownerTag
  }) {

    const trueKeys = isArray(propKeys)
      ? propKeys
      : [propKeys]

    const trueSign = deline(`
      GSNApplyScriptTmpl
        [ownerTag=${ownerTag}]
        [propKeys=${trueKeys}]`)

    const trueAllowFunc = isUndefined(allowKey)
      ? () => true
      : (...values) => !!last(values)

    const truePlh = RegExp(escapeRegExp(scriptPlh), 'g')
    const tmplFunc = (v) => scriptTmpl.replace(truePlh, v)
    const trueScriptFunc = (...values) =>
      trueAllowFunc(...values)
        ? tmplFunc(...values)
        : null

    const trueAllowEmpty = false
    const hasEmpties = (v) => isEmpty(v)

    const wrapH = DefineHandler(trueSign)
    const apply = wrapH('applyScript', applyScriptTmpl)

    apply.tmplType = 'applyScript'
    apply.propKeys = compact(concat(trueKeys, allowKey))
    apply.ownerTag = ownerTag
    apply.destroy = noop
    apply.destroyed = false
    return apply

    function applyScriptTmpl(...values) {
      if (!hasEmpties(...values) || trueAllowEmpty) {
        const src = trueScriptFunc(...values)
        if (!isText(src) && !isEmpty(src)) {
          throw new Error(`${trueSign}: [src=${src}] must be a string or empty!`)
        }

        if (isText(src) && !domh.has.bySrc(src)) {
          const scriptId = uniqueId(addIdPrefix(`applyScriptTmpl_propKeys-${trueKeys}_`))
          const script = domh.createScript(scriptId).attach()
          script.strictSrc(src)
        }
      }
    }
  }
}
